import React, { useEffect, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { APP_NAME } from '../../config/branding';
import {
  closeProWelcome,
  registerProWelcomeController,
} from '../../core/navigation/proWelcomeController';
import { openSettingsOverlay } from '../../core/navigation/settingsOverlayController';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { GlassButton } from '../ui/GlassButton';

/**
 * Post-Checkout celebration.
 * Rendered as an absolute overlay (not RN Modal) so it never fights
 * InAppBrowser / ASWebAuthenticationSession and leaves the UI frozen.
 */
export function ProWelcomeModal() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    registerProWelcomeController({
      open: () => setVisible(true),
      close: () => setVisible(false),
    });
    return () => registerProWelcomeController(null);
  }, []);

  const dismiss = () => {
    setVisible(false);
    closeProWelcome();
  };

  const openManage = () => {
    dismiss();
    setTimeout(() => openSettingsOverlay(), 120);
  };

  if (!visible) {
    return null;
  }

  return (
    <View style={styles.root} pointerEvents="box-none" accessibilityViewIsModal>
      <Pressable style={styles.backdrop} onPress={dismiss} accessibilityLabel="Dismiss" />
      <View style={styles.card} accessibilityRole="alert">
        <Text style={styles.kicker}>Pro</Text>
        <Text style={styles.title}>You’re on Pro</Text>
        <Text style={styles.body}>
          Welcome to {APP_NAME} Pro — household, AI assistant, and more import analyses are
          unlocked for this account.
        </Text>
        <View style={styles.actions}>
          <GlassButton variant="primary" fullWidth onPress={dismiss}>
            Continue
          </GlassButton>
          <GlassButton variant="ghost" fullWidth onPress={openManage}>
            Manage subscription
          </GlassButton>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1000,
    elevation: 1000,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(2, 6, 23, 0.55)',
  },
  card: {
    zIndex: 2,
    width: '100%',
    maxWidth: 400,
    borderRadius: radii.xl,
    borderWidth: 1,
    borderColor: 'rgba(61, 214, 164, 0.35)',
    backgroundColor: colors.surface,
    padding: spacing.lg,
    gap: spacing.sm,
  },
  kicker: {
    ...typography.caption,
    color: colors.accent,
    textTransform: 'uppercase',
    letterSpacing: 1,
    fontWeight: '700',
  },
  title: {
    ...typography.title,
    fontSize: 24,
    color: colors.text,
  },
  body: {
    ...typography.body,
    color: colors.textMuted,
    marginTop: spacing.xs,
  },
  actions: {
    marginTop: spacing.md,
    gap: spacing.sm,
  },
});
